import { useUiModelStore } from "@agent/uiModelStore";
import {
  Callout,
  CardFrame,
  DividerRule,
  FieldList,
  GridBlock,
  ItemList,
  OptionButton,
  PanelSection,
  TableBlock,
  toneStyle,
  UiBadge,
  type UiCardVariant,
} from "@agent/UiPrimitives";
import type { UiNode, UiPayload } from "@agent/uiSchema";
import { usePrincipalKey } from "@auth/sessionStore";
import {
  Button,
  Checkbox,
  cn,
  Input,
  Select,
  Stamp,
} from "@scribenote/brian-design-system";
import { type ReactNode, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export type UiActionHandler = (
  action: string,
  data: Record<string, unknown>,
) => void | Promise<void>;

/** A `button` carrying a `payload` (a self-contained choice) or a
    `description` renders as a tappable choice row, not a footer button — unless
    it sets an explicit `variant`, which marks it a styled action button (e.g. a
    `danger` "Remove" that still carries its target in `payload`). */
const isChoiceButton = (node: UiNode): boolean =>
  node.type === "button" &&
  node.variant === undefined &&
  (node.payload !== undefined || node.description !== undefined);

/** `bind` keys of every `required` field in the tree — form-submitting buttons
    (those without a `payload`) are disabled until each holds a value. Recursion
    keys off the *shape* (anything holding `children`) rather than a list of
    container types, so a node type added later can't silently stop gating the
    submit by being left off the list. */
const collectRequiredBinds = (node: UiNode): string[] => {
  switch (node.type) {
    case "input":
    case "select":
      return node.required ? [node.bind] : [];
    default:
      return "children" in node
        ? (node.children ?? []).flatMap(collectRequiredBinds)
        : [];
  }
};

type UiButtonNode = Extract<UiNode, { type: "button" }>;

const collectButtons = (node: UiNode): UiButtonNode[] =>
  node.type === "button"
    ? [node]
    : "children" in node
      ? (node.children ?? []).flatMap(collectButtons)
      : [];

/** The button Enter presses from a text input: the tree's single completing
    button carrying no `payload` (the plain form submit, `payload == null`
    matching the backend gate's "absent or JSON null"), so type-and-Enter
    sends exactly what clicking it would, never a choice's payload. Zero or
    several candidates leaves Enter inert rather than guessing (WEB-3386). */
const findEnterTarget = (node: UiNode): UiButtonNode | null => {
  const candidates = collectButtons(node).filter(
    (button) => button.completes === true && button.payload == null,
  );
  return candidates.length === 1 ? candidates[0] : null;
};

/** `row` cross-axis alignment. Defaults to `center`, the pre-`align` behaviour. */
const ROW_ALIGN = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  baseline: "items-baseline",
} as const;

// Typographic ramp for `text`: title 24/700 display > heading 18/700 display >
// subheading mono small-caps eyebrow > body 16/400 (inherited, blank for legacy
// payload compat) > caption 12/400.
// Colours live in TEXT_VARIANT_COLOR, not here: emitting both a variant colour
// and a tone colour leaves the winner to stylesheet order, so a tone must
// replace the default, never sit beside it.
const TEXT_VARIANTS = {
  title: "font-display text-2xl font-bold",
  heading: "font-display text-lg font-bold",
  subheading: "text-annotation-caps",
  body: "",
  caption: "text-xs",
} as const;

const TEXT_VARIANT_COLOR: Record<keyof typeof TEXT_VARIANTS, string> = {
  title: "text-ink",
  heading: "text-ink",
  subheading: "text-annotation",
  body: "",
  caption: "text-annotation",
};

// Shared identity for "no entry yet": a fresh `{}` per read would never settle
// under useSyncExternalStore, so submitting a card (whose `reset` drops the
// entry) would spin the renderer until the next payload landed.
const EMPTY_MODEL: Record<string, unknown> = {};

const hasValue = (value: unknown): boolean =>
  typeof value === "string" ? value.trim().length > 0 : value != null;

export const UiRenderer = ({
  payload,
  storeKey,
  onAction,
  allowDismiss = true,
  readOnly = false,
  resolution,
  cardVariant = "default",
  frameless = false,
}: {
  payload: UiPayload;
  /** Stable, globally-unique key for this card's local input state — pass the
      owning task (or view) uuid. Keyed on this rather than `payload.render_id`
      so two cards can never share input state, and a same-card refresh (stable
      render_id) doesn't carry stale text into the updated payload. Namespaced by
      the active principal internally, so callers pass the bare uuid. */
  storeKey: string;
  onAction: UiActionHandler;
  /** When false, suppress the card's dismiss ✕ (e.g. the full notifications
      archive, where dismissal isn't offered). */
  allowDismiss?: boolean;
  /** Display-only mode for persistent SavedViews (WEB-3157): every button is
      rendered inert since a view has no submission flow. Inputs stay editable
      but purely local — their values route nowhere. */
  readOnly?: boolean;
  /** Lock the card and stamp a status word on it when the owning task is
      resolved out-of-band, i.e. the surface knows the card is done (from the
      task's status) but the backend never wrote `submitted_action` into the
      payload; render it inert with a rubber stamp (the mark a person left)
      rather than actionable. The `tone` picks the stamp's ink: `positive`
      (working green, e.g. approved) vs `negative` (stopped red, e.g.
      cancelled). */
  resolution?: { label: string; tone: "positive" | "negative" };
  /** Status chrome for the root `card` frame. Defaults to `default` (untinted);
      a surfaced view passes its severity variant so the view card carries the
      same watch/urgent ring as task cards (WEB-3251). Only the root card takes
      it; nested cards stay default. */
  cardVariant?: UiCardVariant;
  /** Render the root `card`'s contents without its own frame, so a caller can
      embed them inside its own card (e.g. an estimate's notes beside its line
      items). No dismiss, badge, or surface tint — the host card owns those. */
  frameless?: boolean;
}) => {
  // Task/view uuids are clinic-scoped, so a bare `storeKey` hands the next user
  // on a shared device the previous one's unsubmitted text. Namespaced here, not
  // at each call site, so a new surface can't opt out by omission.
  const modelKey = `${usePrincipalKey()}:${storeKey}`;

  const init = useUiModelStore((s) => s.init);
  useEffect(() => init(modelKey, payload.data), [modelKey, payload, init]);

  // A card is answered once the backend stamps `submitted_action`, OR when the
  // surface tells us the owning task is already resolved (`resolution`).
  // Some flows (approving an estimate) close the task without stamping the
  // payload, so status is the only reliable signal. Either way, lock its inputs
  // and buttons so a surface that still shows it can't re-submit a resolved form.
  const answered = payload.submitted_action != null || resolution != null;

  // A card whose only resolution is the dismiss ✕ (no completing button, per
  // the backend's `dismiss_only`) must stay clearable on every surface — the
  // feed passes `allowDismiss={false}`, but an alert can't dead-end there
  // (BE-1877). `readOnly` SavedViews have no dismissal flow, so never force it.
  const dismissOnly = (payload.dismiss_only ?? false) && !readOnly;

  // Read-only views have no submission flow, so Enter routes nowhere there.
  const enterTarget = readOnly ? null : findEnterTarget(payload.root);

  if (frameless) {
    const root = payload.root;
    const children = root.type === "card" ? (root.children ?? []) : [root];
    const requiredBinds = collectRequiredBinds(root);
    return (
      <div className="flex flex-col gap-2 font-body text-ink-body">
        {children.map((child, i) => (
          <Node
            key={i}
            node={child}
            renderId={modelKey}
            cardId={storeKey}
            onAction={onAction}
            requiredBinds={requiredBinds}
            enterTarget={enterTarget}
            allowDismiss={false}
            dismissOnly={false}
            readOnly={readOnly}
            answered={answered}
          />
        ))}
      </div>
    );
  }

  return (
    <Node
      node={payload.root}
      renderId={modelKey}
      cardId={storeKey}
      onAction={onAction}
      requiredBinds={collectRequiredBinds(payload.root)}
      enterTarget={enterTarget}
      allowDismiss={allowDismiss && !readOnly}
      dismissOnly={dismissOnly}
      readOnly={readOnly}
      answered={answered}
      cardVariant={cardVariant}
      badge={
        resolution != null ? (
          <Stamp
            tone={resolution.tone === "negative" ? "stopped" : "working"}
            data-testid={`ui-resolved-${storeKey}`}
          >
            {resolution.label}
          </Stamp>
        ) : undefined
      }
    />
  );
};

const Node = ({
  node,
  renderId,
  cardId,
  onAction,
  requiredBinds,
  enterTarget,
  allowDismiss,
  dismissOnly,
  readOnly,
  answered,
  badge,
  cardVariant = "default",
}: {
  node: UiNode;
  /** Principal-scoped key into the model store. */
  renderId: string;
  /** Bare `storeKey` for the card's test id — keeps principal uuids out of the DOM. */
  cardId: string;
  onAction: UiActionHandler;
  /** `bind` keys of the tree's `required` fields; gate form-submitting buttons. */
  requiredBinds: string[];
  /** The tree's single completing no-`payload` button, pressed by Enter in an
      `input`; null (ambiguous tree or read-only) leaves Enter inert. */
  enterTarget: UiButtonNode | null;
  allowDismiss: boolean;
  /** The card's only resolution is the dismiss ✕ (backend `dismiss_only`): show
      it regardless of the surface's `allowDismiss`, so an alert can't dead-end. */
  dismissOnly: boolean;
  /** Render every button inert (SavedViews have no submission flow). */
  readOnly: boolean;
  /** The card is already answered (`submitted_action` set): disable its inputs
      and buttons so a resolved form can't be re-submitted. */
  answered: boolean;
  /** Status badge for the top-level `card` frame (e.g. "Approved" on a resolved
      estimate). Only the root node carries it; recursive children pass none. */
  badge?: ReactNode;
  /** Status chrome for the root `card` frame (WEB-3251). Like `badge`, only the
      root node carries it; recursive children pass none, so nested cards stay
      untinted. */
  cardVariant?: UiCardVariant;
}) => {
  const model = useUiModelStore((s) => s.models[renderId] ?? EMPTY_MODEL);
  const setValue = useUiModelStore((s) => s.set);
  const reset = useUiModelStore((s) => s.reset);
  const submitting = useUiModelStore((s) => s.submitting[renderId] ?? false);
  const setSubmitting = useUiModelStore((s) => s.setSubmitting);

  const press = async (action: string, data: Record<string, unknown>) => {
    setSubmitting(renderId, true);
    try {
      await onAction(action, data);
      reset(renderId);
    } finally {
      setSubmitting(renderId, false);
    }
  };

  // Layout children inherit cardVariant, so a tree rooted in stack/row/grid
  // still tints its card (BE-1918).
  const layoutChildren = (children: UiNode[] | undefined) =>
    (children ?? []).map((child, i) => (
      <Node
        key={i}
        node={child}
        renderId={renderId}
        cardId={cardId}
        onAction={onAction}
        requiredBinds={requiredBinds}
        enterTarget={enterTarget}
        allowDismiss={allowDismiss}
        dismissOnly={dismissOnly}
        readOnly={readOnly}
        answered={answered}
        cardVariant={cardVariant}
      />
    ));

  // Children of a self-surfacing node. Like `card`, don't forward `cardVariant`,
  // so a nested panel keeps its own look instead of the card's severity tint.
  const surfaceChildren = (children: UiNode[] | undefined) =>
    (children ?? []).map((child, i) => (
      <Node
        key={i}
        node={child}
        renderId={renderId}
        cardId={cardId}
        onAction={onAction}
        requiredBinds={requiredBinds}
        enterTarget={enterTarget}
        allowDismiss={allowDismiss}
        dismissOnly={dismissOnly}
        readOnly={readOnly}
        answered={answered}
      />
    ));

  switch (node.type) {
    case "stack":
    case "row": {
      const className =
        node.type === "row"
          ? [
              "flex",
              ROW_ALIGN[node.align ?? "center"] ?? ROW_ALIGN.center,
              "gap-3",
              node.wrap ? "flex-wrap" : "",
              node.justify === "between" ? "justify-between" : "",
            ]
              .filter(Boolean)
              .join(" ")
          : "flex flex-col gap-3";
      return <div className={className}>{layoutChildren(node.children)}</div>;
    }

    case "grid":
      return (
        <GridBlock columns={node.columns === 3 ? 3 : 2}>
          {layoutChildren(node.children)}
        </GridBlock>
      );

    case "section":
      return (
        <PanelSection title={node.title} icon={node.icon} tone={node.tone}>
          {surfaceChildren(node.children)}
        </PanelSection>
      );

    case "callout":
      return (
        <Callout tone={node.tone} title={node.title} icon={node.icon}>
          {surfaceChildren(node.children)}
        </Callout>
      );

    case "fields":
      return <FieldList items={node.items} />;

    case "list":
      return <ItemList items={node.items} divided={node.divided} />;

    case "divider":
      return <DividerRule />;

    case "text": {
      // Rendered as markdown so any view can carry prose (SOAP notes, discharge
      // summaries) through the same primitive. `.ds-markdown p` inherits colour
      // and size, so the wrapper's variant/tone classes reach the prose inside.
      const tone = toneStyle(node.tone);
      const body = (
        <div className="ds-markdown min-w-0">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{node.text}</ReactMarkdown>
        </div>
      );

      // A heading gets a leading accent dot, indenting it to line up with the
      // option chips below. The dot never stands alone: it rides beside the
      // heading text, and a tone inks both together.
      if (node.variant === "heading")
        return (
          <div
            className={`${TEXT_VARIANTS.heading} ${tone.text || "text-ink"}`}
          >
            {body}
          </div>
        );

      const variant = node.variant ?? "body";
      const className = `${TEXT_VARIANTS[variant] ?? ""} ${
        tone.text || TEXT_VARIANT_COLOR[variant] || ""
      }`.trim();
      // Untoned body text renders bare, unchanged from before variants existed.
      return className ? <div className={className}>{body}</div> : body;
    }

    case "badge":
      // Display-only leaf (e.g. a summary alert's "Safety" tag). No store
      // interaction — badges never bind or act. The testid keys off the
      // variant (a fixed token) rather than the freeform label text; badges
      // sharing a variant are expected (getAllBy in tests).
      return (
        <UiBadge
          variant={node.variant}
          icon={node.icon}
          text={node.text}
          dataTestId={`ui-badge-${node.variant ?? "default"}`}
        />
      );

    case "input":
      return (
        <Input
          data-testid={`ui-${node.bind}`}
          placeholder={node.placeholder}
          value={(model[node.bind] as string) ?? ""}
          disabled={answered}
          onChange={(e) => setValue(renderId, node.bind, e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
            e.preventDefault();
            // Same gate as clicking the target itself, plus this input must
            // hold a value: Enter in an empty field submits nothing.
            if (
              enterTarget === null ||
              submitting ||
              answered ||
              !hasValue(model[node.bind]) ||
              requiredBinds.some((bind) => !hasValue(model[bind]))
            )
              return;
            void press(enterTarget.action, { ...model });
          }}
        />
      );

    case "select":
      return (
        <Select
          data-testid={`ui-${node.bind}`}
          label={node.label}
          options={node.options}
          value={(model[node.bind] as string) ?? null}
          disabled={answered}
          onValueChange={(value) => setValue(renderId, node.bind, value)}
        />
      );

    case "checkbox":
      return (
        <Checkbox
          data-testid={`ui-${node.bind}`}
          label={node.text}
          checked={Boolean(model[node.bind])}
          disabled={answered}
          onChange={(e) => setValue(renderId, node.bind, e.target.checked)}
        />
      );

    case "button": {
      // Pressing a button merges its `payload` over the current field values —
      // a choice button answers with which choice it is, a plain submit sends
      // the form as-is. The dock maps the emitted action to PATCH (or, for
      // `dismiss`, a browser-only dismissal); whether the press completes the
      // task is the backend's read of the button's `completes` flag.
      const data = { ...model, ...node.payload };

      if (isChoiceButton(node)) {
        // A self-contained choice — never gated on required fields.
        const optionId =
          typeof node.payload?.selected_option_id === "string"
            ? node.payload.selected_option_id
            : node.action;
        return (
          <OptionButton
            text={node.text}
            description={node.description}
            disabled={submitting || readOnly || answered}
            dataTestId={`ui-option-${optionId}`}
            onPress={() => void press(node.action, data)}
          />
        );
      }

      // A form-submitting press with required fields still empty would consume
      // the card server-side while answering the agent with holes, so keep the
      // button disabled until every `required` field holds a value. A button
      // carrying a `payload` is a self-contained action (e.g. a danger Remove),
      // not a form submit, so it is never gated on the card's required fields.
      const missingRequired =
        node.payload === undefined &&
        requiredBinds.some((bind) => !hasValue(model[bind]));
      const inert = submitting || missingRequired || readOnly || answered;

      // Danger is expressed in ink, not paper: a destructive action is the
      // rubber-stamp outline from ConfirmDialog's destructive confirm, mono
      // caps in stopped ink over the outline button, never a red fill. The
      // colors drop while the button is inert so it reads inert.
      if (node.variant === "danger")
        return (
          <Button
            variant="outline"
            data-testid={`ui-action-${node.action}`}
            disabled={inert}
            onClick={() => void press(node.action, data)}
            className={cn(
              "font-mono uppercase tracking-[0.13em]",
              !inert && "border-ink-stopped text-ink-stopped",
            )}
          >
            {node.text}
          </Button>
        );

      return (
        <Button
          variant={node.variant === "secondary" ? "outline" : "primary"}
          data-testid={`ui-action-${node.action}`}
          disabled={inert}
          onClick={() => void press(node.action, data)}
        >
          {node.text}
        </Button>
      );
    }

    case "card": {
      // The agent composes the card; we just supply the styled frame (+ optional
      // dismiss) and render its children.
      return (
        <CardFrame
          id={cardId}
          // An alert (no completing action) is always clearable; otherwise the
          // ✕ follows the surface's policy and the agent's `dismissible` opt-in
          // — so the feed still suppresses it on cards resolvable by a button.
          dismissible={
            dismissOnly || (allowDismiss && (node.dismissible ?? false))
          }
          onDismiss={() => void onAction("dismiss", {})}
          badge={badge}
          variant={cardVariant}
        >
          {surfaceChildren(node.children)}
        </CardFrame>
      );
    }

    case "table":
      return <TableBlock columns={node.columns} rows={node.rows} />;

    default:
      // Exhaustiveness check: `node` only narrows to `never` if every union
      // case is handled above, so an unrendered node type fails the build. At
      // runtime, an unknown node (newer backend payload) renders as nothing.
      return renderNothing(node);
  }
};

const renderNothing = (_node: never): null => null;
